import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import './HomeProspector.css';

const ZIP_OPTIONS = ['30307', '30306', '30317', '30316', '30312', '30308', '30318'];
const PROPERTY_TYPES = ['All', 'SFR', 'Townhome'];
const CONFIDENCE_LEVELS = [
  ['all', 'All'],
  ['high', 'High'],
  ['medium', 'Medium'],
  ['low', 'Low'],
];
const BEDS_OPTIONS = [
  ['any', 'Any'],
  ['2', '2+'],
  ['3', '3+'],
];

const CONFIDENCE_BADGE = {
  high: { label: 'High Confidence', className: 'badge-green' },
  medium: { label: 'Medium Confidence', className: 'badge-amber' },
  low: { label: 'Low Confidence', className: 'badge-gray' },
};

function formatMonthYear(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
}

function vacancyBarColor(score) {
  const pct = score * 100;
  if (pct >= 70) return 'var(--red)';
  if (pct >= 50) return 'var(--amber)';
  return 'var(--green)';
}

function SkeletonCard() {
  return (
    <div className="hp-card hp-skeleton">
      <div className="hp-skeleton-line hp-skeleton-badge" />
      <div className="hp-skeleton-line hp-skeleton-title" />
      <div className="hp-skeleton-line hp-skeleton-sub" />
      <div className="hp-skeleton-line hp-skeleton-bar" />
      <div className="hp-skeleton-line hp-skeleton-footer" />
    </div>
  );
}

function ProspectCard({ candidate, onRequestInfo }) {
  const navigate = useNavigate();
  const confidenceInfo = CONFIDENCE_BADGE[candidate.confidence] || CONFIDENCE_BADGE.low;
  const vacancyPct = Math.round((candidate.vacancy_score || 0) * 100);
  const lastRented = formatMonthYear(candidate.last_listed_date);

  return (
    <div className="hp-card">
      <div className="hp-card-top">
        <span className={`badge ${confidenceInfo.className}`}>{confidenceInfo.label}</span>
        {candidate.predicted_window && (
          <span className="badge badge-blue">{candidate.predicted_window.replace('-', '–')}</span>
        )}
      </div>

      <div className="hp-address">{candidate.address}</div>
      <div className="hp-subtitle">
        {[candidate.neighborhood, candidate.zip].filter(Boolean).join(' · ')}
      </div>

      <div className="hp-meta-row">
        {candidate.property_type && <span>{candidate.property_type}</span>}
        {candidate.beds != null && <span>{candidate.beds} bd</span>}
        {candidate.baths != null && <span>{candidate.baths} ba</span>}
        {candidate.sqft != null && <span>{candidate.sqft.toLocaleString()} sqft</span>}
      </div>

      <div className="hp-vacancy">
        <div className="hp-vacancy-label">
          <span>vacancy likelihood</span>
          <span className="hp-vacancy-pct">{vacancyPct}%</span>
        </div>
        <div className="hp-vacancy-track">
          <div
            className="hp-vacancy-fill"
            style={{ width: `${vacancyPct}%`, background: vacancyBarColor(candidate.vacancy_score) }}
          />
        </div>
      </div>

      {lastRented && <div className="hp-last-rented">Last rented {lastRented}</div>}

      <div className="hp-chip-row">
        {candidate.avg_lease_length != null && (
          <span className="badge badge-gray">Avg lease: {candidate.avg_lease_length} mo</span>
        )}
        {candidate.cycle_count != null && (
          <span className="badge badge-gray">Listed {candidate.cycle_count} times</span>
        )}
      </div>

      {candidate.community_name && (
        <div className="hp-community-badge">{candidate.community_name} community</div>
      )}

      {candidate.est_rent != null && (
        <div className="hp-rent">${candidate.est_rent.toLocaleString()} / mo</div>
      )}

      <div className="hp-actions">
        <button
          className="btn btn-primary btn-full"
          onClick={() => navigate(`/bid?address=${encodeURIComponent(candidate.address)}`)}
        >
          Place Early Bid →
        </button>
        <button className="btn btn-full" onClick={() => onRequestInfo(candidate.id)}>
          Request Info
        </button>
      </div>
    </div>
  );
}

export default function HomeProspector() {
  const [candidates, setCandidates] = useState([]);
  const [runDate, setRunDate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedZips, setSelectedZips] = useState(() => new Set(ZIP_OPTIONS));
  const [propertyType, setPropertyType] = useState('All');
  const [confidence, setConfidence] = useState('all');
  const [minBeds, setMinBeds] = useState('any');
  const [toast, setToast] = useState('');
  const toastTimeoutRef = useRef(null);

  useEffect(() => {
    fetchCandidates();
  }, []);

  useEffect(() => {
    if (window.fbq) {
      window.fbq('trackCustom', 'HomeProspectorView', { zip_codes: Array.from(selectedZips) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function fetchCandidates() {
    setLoading(true);

    const { data: latest } = await supabase
      .from('scored_candidates')
      .select('run_date')
      .order('run_date', { ascending: false })
      .limit(1);

    const latestRunDate = latest?.[0]?.run_date || null;
    setRunDate(latestRunDate);

    if (!latestRunDate) {
      setCandidates([]);
      setLoading(false);
      return;
    }

    const { data } = await supabase
      .from('scored_candidates')
      .select('*')
      .eq('run_date', latestRunDate)
      .order('vacancy_score', { ascending: false });

    setCandidates(data || []);
    setLoading(false);
  }

  function toggleZip(zip) {
    setSelectedZips(prev => {
      const next = new Set(prev);
      if (next.has(zip)) next.delete(zip);
      else next.add(zip);
      return next;
    });
  }

  function handleRequestInfo() {
    setToast('Feature coming soon');
    window.clearTimeout(toastTimeoutRef.current);
    toastTimeoutRef.current = window.setTimeout(() => setToast(''), 2500);
  }

  useEffect(() => {
    return () => window.clearTimeout(toastTimeoutRef.current);
  }, []);

  const filtered = useMemo(() => {
    return candidates.filter(c => {
      if (!selectedZips.has(c.zip)) return false;
      if (propertyType !== 'All' && c.property_type !== propertyType) return false;
      if (confidence !== 'all' && c.confidence !== confidence) return false;
      if (minBeds !== 'any' && (c.beds == null || c.beds < Number(minBeds))) return false;
      return true;
    });
  }, [candidates, selectedZips, propertyType, confidence, minBeds]);

  return (
    <div className="page hp-page">
      <div className="hp-header">
        <div>
          <h1 className="hp-title">HomeProspector</h1>
          <p className="hp-tagline">Predicted rental vacancies before they hit the market.</p>
        </div>
        {runDate && (
          <div className="badge badge-blue hp-rundate-badge">
            updated {new Date(runDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })}
          </div>
        )}
      </div>

      <div className="hp-filters card">
        <div className="hp-filter-group">
          <div className="hp-filter-label">zip codes</div>
          <div className="hp-pill-row">
            {ZIP_OPTIONS.map(zip => (
              <div
                key={zip}
                className={`hp-zip-pill ${selectedZips.has(zip) ? 'active' : ''}`}
                onClick={() => toggleZip(zip)}
              >
                {zip}
              </div>
            ))}
          </div>
        </div>

        <div className="hp-filter-row">
          <div className="hp-filter-group">
            <div className="hp-filter-label">property type</div>
            <div className="hp-segment">
              {PROPERTY_TYPES.map(val => (
                <button
                  key={val}
                  className={propertyType === val ? 'active' : ''}
                  onClick={() => setPropertyType(val)}
                >
                  {val}
                </button>
              ))}
            </div>
          </div>

          <div className="hp-filter-group">
            <div className="hp-filter-label">confidence</div>
            <div className="hp-segment">
              {CONFIDENCE_LEVELS.map(([val, label]) => (
                <button
                  key={val}
                  className={confidence === val ? 'active' : ''}
                  onClick={() => setConfidence(val)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div className="hp-filter-group">
            <div className="hp-filter-label">beds</div>
            <div className="hp-segment">
              {BEDS_OPTIONS.map(([val, label]) => (
                <button
                  key={val}
                  className={minBeds === val ? 'active' : ''}
                  onClick={() => setMinBeds(val)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="hp-count">
        {loading ? 'loading properties...' : `Showing ${filtered.length} propert${filtered.length === 1 ? 'y' : 'ies'}`}
      </div>

      {loading ? (
        <div className="hp-grid">
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
        </div>
      ) : filtered.length === 0 ? (
        <div className="card hp-empty">
          No properties match your filters. Try broadening your search.
        </div>
      ) : (
        <div className="hp-grid">
          {filtered.map(candidate => (
            <ProspectCard key={candidate.id} candidate={candidate} onRequestInfo={handleRequestInfo} />
          ))}
        </div>
      )}

      {toast && <div className="hp-toast">{toast}</div>}
    </div>
  );
}
